import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import { normalizeBusinessPlanSectionMarkdown } from "@/utils/business-plan-markdown.utils";

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h3 className="mt-6 mb-3 text-xl font-bold text-white first:mt-0">{children}</h3>
  ),
  h2: ({ children }) => (
    <h4 className="mt-5 mb-2 text-lg font-bold text-white first:mt-0">{children}</h4>
  ),
  h3: ({ children }) => (
    <h5 className="mt-4 mb-2 text-base font-semibold text-white/90 first:mt-0">{children}</h5>
  ),
  p: ({ children }) => <p className="mb-4 leading-7 text-white/70 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="mb-4 list-disc space-y-2 pl-5 text-white/70">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-4 list-decimal space-y-2 pl-5 text-white/70">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-7">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-white/90">{children}</strong>,
  em: ({ children }) => <em className="text-white/75">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} className="text-brand underline underline-offset-2 hover:text-brand-muted">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-2 border-brand/40 pl-4 text-white/60 italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-white/10" />,
  code: ({ className, children }) => {
    const isBlock = Boolean(className);
    if (isBlock) {
      return (
        <code className="block overflow-x-auto rounded-lg bg-white/5 p-3 text-sm text-white/80">
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-white/10 px-1.5 py-0.5 text-sm text-white/85">{children}</code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-4 overflow-x-auto rounded-lg bg-white/5 p-3 text-sm">{children}</pre>
  ),
  table: ({ children }) => (
    <div className="mb-4 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-white/10">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-white/5">{children}</tr>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-white/45">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="px-3 py-2 text-white/70">{children}</td>,
};

export type BusinessPlanMarkdownProps = {
  content: string;
  sectionLabel?: string;
};

export const BusinessPlanMarkdown: React.FC<BusinessPlanMarkdownProps> = ({
  content,
  sectionLabel,
}) => {
  const markdown = normalizeBusinessPlanSectionMarkdown(content, { sectionLabel });
  if (!markdown) {
    return null;
  }

  return (
    <div className="bp-prose">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
};
